import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { Skeleton } from '@heyroot/ui/primitives/Skeleton';
import { Tabs, Tab } from '@heyroot/ui/primitives/Tabs';
import {
  fetchBusinessType,
  createDraftVersion,
  publishVersion,
  setBusinessTypeActive,
  validateVersion,
  queryKeys,
  AdminApiError,
  type ValidationIssue,
  type BusinessTypeDetail,
} from '../lib/api';
import { VersionRail } from '../components/business-types/version-rail';
import { ModuleSetEditor } from '../components/business-types/module-set-editor';
import { RoleTemplateEditor } from '../components/business-types/role-template-editor';

const MODULE_CODES = new Set(['NO_MODULES', 'UNKNOWN_MODULE']);
const ROLE_CODES = new Set([
  'NO_ROLES',
  'ROLE_WITHOUT_PERMISSIONS',
  'DUPLICATE_ROLE',
  'UNKNOWN_PERMISSION',
  'PLATFORM_ONLY_PERMISSION',
]);

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="rounded-control border border-border bg-surface p-3">
      <dt className="text-eyebrow uppercase text-muted">{label}</dt>
      <dd className="mt-1.5 text-body-sm text-primary">
        {value ?? <span className="text-muted">—</span>}
      </dd>
    </div>
  );
}

export function BusinessTypeDetailPage() {
  const { businessType: slug } = useParams({ strict: false }) as { businessType: string };
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  // Issues from a rejected publish -- merged with the proactive validate
  // query below, since the 422's per-issue array is the only source when the
  // proactive query hasn't (re)loaded yet.
  const [publishErrorIssues, setPublishErrorIssues] = useState<ValidationIssue[]>([]);

  const { data, isPending, isError, error } = useQuery({
    queryKey: queryKeys.businessType(slug),
    queryFn: () => fetchBusinessType(slug),
  });

  // Default selection: the draft if one exists (that is what an operator came
  // to edit), else the published version, else the newest.
  const selected =
    data?.versions.find((v) => v.id === selectedId) ??
    data?.versions.find((v) => v.status === 'draft') ??
    data?.versions.find((v) => v.status === 'published') ??
    data?.versions[0] ??
    null;
  const isDraft = selected?.status === 'draft';

  const { data: validation } = useQuery({
    queryKey: ['business-type-validate', selected?.id],
    queryFn: () => validateVersion(selected!.id),
    enabled: Boolean(selected) && isDraft,
  });
  const issues: ValidationIssue[] = useMemo(() => {
    const base = validation?.data.issues ?? [];
    if (publishErrorIssues.length === 0) return base;
    const seen = new Set(base.map((i) => `${i.code}:${i.message}`));
    const merged = [...base];
    for (const i of publishErrorIssues) {
      const key = `${i.code}:${i.message}`;
      if (!seen.has(key)) {
        merged.push(i);
        seen.add(key);
      }
    }
    return merged;
  }, [validation, publishErrorIssues]);
  const moduleIssues = issues.filter((i) => MODULE_CODES.has(i.code));
  const roleIssues = issues.filter((i) => ROLE_CODES.has(i.code));
  // Anything unrouted still has to be visible -- a new FSM code must never be
  // silently swallowed by this map.
  const otherIssues = issues.filter((i) => !MODULE_CODES.has(i.code) && !ROLE_CODES.has(i.code));

  const createDraft = useMutation({
    mutationFn: () => createDraftVersion(data!.id),
    onSuccess: async (res) => {
      setNotice(null);
      await qc.invalidateQueries({ queryKey: queryKeys.businessType(slug) });
      setSelectedId(res.data.id);
      setTab(0);
    },
    onError: async (err) => {
      if (err instanceof AdminApiError && err.code === 'DRAFT_EXISTS') {
        // Correction: that's only true of the repository function. The route
        // maps a 23505 race to this 409, so the cache here has no draft yet --
        // refetch first and only claim to open one if the refetch actually
        // finds it.
        const result = await qc.fetchQuery({
          queryKey: queryKeys.businessType(slug),
          queryFn: () => fetchBusinessType(slug),
        });
        const existing = (result as BusinessTypeDetail | undefined)?.versions.find(
          (v) => v.status === 'draft',
        );
        if (existing) {
          setNotice('A draft already exists — opening it.');
          setSelectedId(existing.id);
        } else {
          setNotice(
            'A draft was reported to exist, but none was found after refreshing. Try again.',
          );
        }
        return;
      }
      setNotice(err instanceof Error ? err.message : 'Could not create a draft.');
    },
  });

  const publish = useMutation({
    mutationFn: (changeSummary: string | null) => publishVersion(selected!.id, changeSummary),
    onSuccess: async () => {
      setNotice(null);
      setPublishErrorIssues([]);
      await qc.invalidateQueries({ queryKey: queryKeys.businessType(slug) });
      await qc.invalidateQueries({ queryKey: ['business-types'] });
      // Re-render with the promoted version selected -- it's still the same
      // version id, only its status flips to 'published'.
      setSelectedId(selected!.id);
    },
    onError: (err) => {
      if (err instanceof AdminApiError && err.code === 'VERSION_NOT_EDITABLE') {
        // Client/server disagreed about status; a refetch resolves it.
        void qc.invalidateQueries({ queryKey: queryKeys.businessType(slug) });
        return;
      }
      if (err instanceof AdminApiError && err.issues.length > 0) {
        // Merge into the same routed sets the proactive validate query feeds --
        // the 422's own per-issue array is what the module deliberately builds,
        // and it's the only source when the proactive query hasn't reloaded.
        setPublishErrorIssues(err.issues);
      }
      setNotice(err instanceof Error ? err.message : 'Could not publish.');
    },
  });

  const deactivate = useMutation({
    mutationFn: (isActive: boolean) => setBusinessTypeActive(data!.id, isActive),
    onSuccess: async () => {
      setConfirmDeactivate(false);
      setDeactivateError(null);
      await qc.invalidateQueries({ queryKey: queryKeys.businessType(slug) });
      await qc.invalidateQueries({ queryKey: ['business-types'] });
    },
    onError: (err) => {
      // Keep the dialog open -- this is the most consequential control on the
      // surface and a silent failure would leave the operator unsure whether
      // it went through.
      setDeactivateError(err instanceof Error ? err.message : 'Could not update status.');
    },
  });

  useEffect(() => {
    // A newly-selected version's tabs should reflect its own issues, not a
    // stale selection from the previous version.
    setNotice(null);
    setPublishErrorIssues([]);
  }, [selected?.id]);

  if (isPending) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-6 w-40" />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-[240px_1fr]">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (isError) {
    const notFound = error instanceof AdminApiError && error.status === 404;
    return (
      <div className="flex flex-col gap-5">
        <Link
          to="/business-types"
          className="inline-flex items-center gap-1 text-body-sm text-secondary hover:text-primary"
        >
          <ChevronLeft size={14} /> Business Types
        </Link>
        {notFound ? (
          <EmptyState title="Not found" description="No such business type." />
        ) : (
          <div
            role="alert"
            className="rounded-control border border-danger bg-danger-pale text-danger-text text-body-sm p-4"
          >
            {error instanceof Error ? error.message : 'Failed to load business type.'}
          </div>
        )}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/business-types"
        className="inline-flex items-center gap-1 text-body-sm text-secondary hover:text-primary"
      >
        <ChevronLeft size={14} /> Business Types
      </Link>

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-heading-2 text-primary">{data.name}</h1>
            {data.isActive ? (
              <Badge variant="neutral">Active</Badge>
            ) : (
              <Badge variant="danger">Deactivated</Badge>
            )}
          </div>
          <p className="mt-1 text-body-sm text-muted">{data.slug}</p>
          {data.description && <p className="mt-1 text-body-sm text-muted">{data.description}</p>}
        </div>
        <div className="flex items-center gap-2">
          {!isDraft && selected && (
            <Button onClick={() => createDraft.mutate()} loading={createDraft.isPending}>
              Create draft
            </Button>
          )}
          {isDraft && selected && (
            <Button
              disabled={issues.length > 0 || validation === undefined || publish.isPending}
              onClick={() => publish.mutate(null)}
              loading={publish.isPending}
              title={
                issues.length > 0
                  ? 'Resolve the validation issues first'
                  : validation === undefined
                    ? 'Validating…'
                    : undefined
              }
            >
              Publish v{selected.versionNumber}
            </Button>
          )}
          <Button
            type="button"
            variant={data.isActive ? 'danger' : 'secondary'}
            onClick={() => {
              setDeactivateError(null);
              setConfirmDeactivate(true);
            }}
          >
            {data.isActive ? 'Deactivate' : 'Reactivate'}
          </Button>
        </div>
      </div>

      {notice && (
        <p role="status" className="rounded-control bg-info-pale p-3 text-body-sm text-info-text">
          {notice}
        </p>
      )}

      {confirmDeactivate && (
        <div
          role="alertdialog"
          aria-label={data.isActive ? 'Confirm deactivate' : 'Confirm reactivate'}
          className="flex flex-col gap-3 rounded-control border border-danger bg-danger-pale p-4"
        >
          {data.isActive ? (
            <p className="text-body-sm text-danger-text">
              Deactivating <span className="text-body-sm-emphasis">{data.name}</span> removes it
              from what signup offers going forward. Tenants already on this type keep their slug
              and are unaffected — this only stops{' '}
              <em className="not-italic text-body-sm-emphasis">new</em> tenants from picking it.
            </p>
          ) : (
            <p className="text-body-sm text-secondary">
              Reactivate <span className="text-body-sm-emphasis">{data.name}</span> so it can be
              offered at signup again (subject to its own signup-visibility setting).
            </p>
          )}
          {deactivateError && (
            <p role="alert" className="text-body-sm text-danger-text">
              {deactivateError}
            </p>
          )}
          <div className="flex gap-2">
            <Button
              variant={data.isActive ? 'danger' : 'primary'}
              loading={deactivate.isPending}
              onClick={() => deactivate.mutate(!data.isActive)}
            >
              {data.isActive ? 'Deactivate' : 'Reactivate'}
            </Button>
            <Button variant="secondary" onClick={() => setConfirmDeactivate(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {otherIssues.length > 0 && (
        <ul
          role="alert"
          className="flex flex-col gap-1 rounded-control border border-danger bg-danger-pale p-3 text-body-sm text-danger-text"
        >
          {otherIssues.map((i, idx) => (
            <li key={`${i.code}-${idx}`}>
              [{i.code}] {i.message}
            </li>
          ))}
        </ul>
      )}

      {data.versions.length === 0 ? (
        <EmptyState
          title="No template yet"
          description="Create a draft to define its modules and role templates."
          action={
            <Button onClick={() => createDraft.mutate()} loading={createDraft.isPending}>
              Create draft
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-[240px_1fr] md:items-start">
          <VersionRail
            versions={data.versions}
            selectedId={selected?.id ?? null}
            onSelect={(id) => {
              setSelectedId(id);
              setTab(0);
            }}
          />

          {selected && (
            <div className="min-w-0 rounded-control border border-border bg-surface p-4">
              {!isDraft && (
                <p className="mb-3 text-body-sm text-muted">
                  v{selected.versionNumber} is {selected.status} and read-only. Create a draft to
                  make changes.
                </p>
              )}
              <Tabs activeIndex={tab} onChange={setTab}>
                <Tab
                  label={
                    <span className="flex items-center gap-1.5">
                      Modules
                      {moduleIssues.length > 0 && (
                        <Badge variant="danger">{moduleIssues.length}</Badge>
                      )}
                    </span>
                  }
                >
                  <ModuleSetEditor
                    slug={slug}
                    version={selected}
                    isDraft={isDraft}
                    issues={moduleIssues}
                  />
                </Tab>
                <Tab
                  label={
                    <span className="flex items-center gap-1.5">
                      Roles
                      {roleIssues.length > 0 && <Badge variant="danger">{roleIssues.length}</Badge>}
                    </span>
                  }
                >
                  <RoleTemplateEditor
                    slug={slug}
                    version={selected}
                    isDraft={isDraft}
                    issues={roleIssues}
                  />
                </Tab>
                <Tab label="Accounting">
                  <div className="flex flex-col gap-4">
                    <p className="text-body-sm text-muted">
                      Read-only. These change by publishing a new version.
                    </p>
                    <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field label="Tax provider" value={selected.taxProvider} />
                      <Field label="Receipt template" value={selected.receiptTemplate} />
                    </dl>
                    <Field
                      label="Course definitions"
                      value={
                        Object.keys(selected.courseDefinitions).length
                          ? JSON.stringify(selected.courseDefinitions)
                          : null
                      }
                    />
                  </div>
                </Tab>
              </Tabs>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
